import React, { useState, useEffect } from 'react';
import { View, ActivityIndicator, ScrollView, TouchableOpacity } from 'react-native';
import { translate } from '../../../services/translate';
import BasicModal from '../../../components/BasicModal';
import MyAppText from '../../../components/MyAppText';
import { cameraService } from '../../../services/central-api/cameras';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import CollapsibleFilter from '../../../components/Filters/Dispatch/CollapsibleFilter';
import FilterCheckboxList, { FilterListItem } from '../../../components/Filters/FilterCheckboxList';
import { useAuth } from '../../../context/Auth';

export enum ShareType {
    USER = 'USER',
    ACTING_BODY = 'ACTING_BODY',
    ACTING_BODY_UNIT = 'ACTING_BODY_UNIT',
}

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    mosaic?: Mosaic;
}

export default function MosaicShareModal({ onClose, mosaic, isModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [openFilter, setOpenFilter] = useState<string | null>('share');

    const [shareType, setShareType] = useState<ShareType>(ShareType.ACTING_BODY);

    const [selectedActingBody, setSelectedActingBody] = useState<number>(0);
    const [selectedActingBodyUnit, setSelectedActingBodyUnit] = useState<number>(0);
    const [selectedUsers, setSelectedUsers] = useState<number[]>([]);

    const [selectableActingBodies, setSelectableActingBodies] = useState<FilterListItem[]>([]);
    const [allActingBodiesUnits, setAllActingBodiesUnits] = useState<{ id: number; name: string; actingBodyId: number; }[]>([]);

    const [usersByActingBodyUnit, setUsersByActingBodyUnit] = useState<Record<number, FilterListItem[]>>({});
    const [loadingActingBodyUnitId, setLoadingActingBodyUnitId] = useState<number>(0);

    const mode: 'global' | 'body' | 'unit' =
        authenticatedUser?.permissions?.share_mosaic_with_all_acting_bodies ? 'global'
            : authenticatedUser?.permissions?.share_mosaic_with_same_acting_body ? 'body'
                : 'unit';

    function cleanValues() {
        setOpenFilter('share');
        setShareType(ShareType.ACTING_BODY);
        setSelectedActingBody(0);
        setSelectedActingBodyUnit(0);
        setSelectedUsers([]);
        setSelectableActingBodies([]);
        setAllActingBodiesUnits([]);
        setUsersByActingBodyUnit({});
        setLoadingActingBodyUnitId(0);
    }

    async function loadPageInfo() {
        try {
            setIsLoading(true);

            const [actingBodies, actingBodyUnits] = await Promise.all([
                cameraService.getActingBodiesToShareMosaic().catch(() => []),
                cameraService.getActingBodyUnitsToShareMosaic().catch(() => []),
            ]);

            setSelectableActingBodies(actingBodies.map(actingBody => ({
                label: actingBody.name,
                value: actingBody.id,
            })));

            setAllActingBodiesUnits(actingBodyUnits);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    function toggleFilter(key: string) {
        setOpenFilter(prev => prev === key ? null : key);
    }

    async function loadActingBodyUnitUsers(ActingBodyUnitId: number) {
        if (usersByActingBodyUnit[ActingBodyUnitId]) {
            return;
        }
        try {
            setLoadingActingBodyUnitId(ActingBodyUnitId);
            const users = await cameraService.getAvailableUsersToShareMosaic({ actingBodyUnitId: ActingBodyUnitId });
            setUsersByActingBodyUnit(prev => ({
                ...prev,
                [ActingBodyUnitId]: users.map(user => ({
                    label: `${user.warName?.trim() ? user.warName : user.name} - RF: ${user.registry}`,
                    value: user.id,
                })),
            }));
        } catch (err) {
            console.error(err);
            setUsersByActingBodyUnit(prev => ({ ...prev, [ActingBodyUnitId]: [] }));
        } finally {
            setLoadingActingBodyUnitId(0);
        }
    }

    function selectActingBody(actingBodyId: number) {
        const next = selectedActingBody === actingBodyId ? 0 : actingBodyId;
        setSelectedActingBody(next);
        setSelectedActingBodyUnit(0);
        setSelectedUsers([]);
        setShareType(ShareType.ACTING_BODY);
    }

    function selectUnit(unitId: number) {
        const next = selectedActingBodyUnit === unitId ? 0 : unitId;
        setSelectedActingBodyUnit(next);
        setSelectedUsers([]);
        if (next) {
            setShareType(ShareType.ACTING_BODY_UNIT);
            loadActingBodyUnitUsers(next);
        } else {
            setShareType(ShareType.ACTING_BODY);
        }
    }

    function changeUsers(ids: number[]) {
        setSelectedUsers(ids);
        setShareType(ids.length ? ShareType.USER : ShareType.ACTING_BODY_UNIT);
    }

    const hasSelection =
        (shareType === ShareType.USER && selectedUsers.length > 0) ||
        (shareType === ShareType.ACTING_BODY_UNIT && selectedActingBodyUnit > 0) ||
        (shareType === ShareType.ACTING_BODY && selectedActingBody > 0);

    async function formSubmit(): Promise<void | false> {
        if (!mosaic || !hasSelection) {
            return false;
        }

        let targetIds: number[] = [];
        if (shareType === ShareType.USER) {
            targetIds = selectedUsers;
        } else if (shareType === ShareType.ACTING_BODY_UNIT) {
            targetIds = [selectedActingBodyUnit];
        } else {
            targetIds = [selectedActingBody];
        }

        try {
            await cameraService.shareMosaic(mosaic.id, shareType, targetIds);

            cleanValues();

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
        } catch (err) {
            if (err instanceof ClientError) {
                Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
                return false;
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            return false;
        }
    }

    const actingBodiesUnitsOf = (actingBodyId: number) => allActingBodiesUnits.filter(unit => unit.actingBodyId === actingBodyId);

    function renderActingBodyUnit(actingBodyUnit: { id: number; name: string; actingBodyId: number; }) {
        const isSelected = selectedActingBodyUnit === actingBodyUnit.id;
        const users = usersByActingBodyUnit[actingBodyUnit.id] ?? [];
        return (
            <View key={actingBodyUnit.id}>
                <TouchableOpacity style={styles.unitRow} onPress={() => selectUnit(actingBodyUnit.id)}>
                    <View style={styles.unitLeft}>
                        <View style={[styles.radio, isSelected && styles.radioSelected]}>
                            {isSelected && <View style={styles.radioInner} />}
                        </View>
                        <MyAppText style={styles.unitLabel}>{actingBodyUnit.name}</MyAppText>
                    </View>
                    <FontAwesomeIcon
                        icon={(isSelected ? faChevronUp : faChevronDown) as IconDefinition}
                        fontSize={12}
                        color={theme.colors.primary['500']}
                    />
                </TouchableOpacity>

                {isSelected && (
                    <View style={styles.usersContainer}>
                        {loadingActingBodyUnitId === actingBodyUnit.id ? (
                            <ActivityIndicator size='small' color={theme.colors.loadingColor} />
                        ) : users.length === 0 ? (
                            <MyAppText style={styles.emptyText}>{translate('noUsersInUnit')}</MyAppText>
                        ) : (
                            <FilterCheckboxList
                                items={users}
                                value={selectedUsers}
                                setValue={(next) => {
                                    const ids = typeof next === 'function' ? next(selectedUsers) : next;
                                    changeUsers(ids);
                                }}
                            />
                        )}
                    </View>
                )}
            </View>
        );
    }

    const ownActingBodyUnit = allActingBodiesUnits.find(u => u.id === authenticatedUser?.actingBodyUnitId);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={(visible) => {
                if (!visible) {
                    onClose();
                }
            }}
            title={translate('share_mosaic')}
            width='large'
            mobileHeight='fill'
            disableScrollView
            cancelButton={{ onClick: cleanValues }}
            primaryButton={{
                text: translate('share_mosaic'),
                onClick: formSubmit,
                disabled: !hasSelection || isLoading,
            }}
        >
            <View style={{ flex: 1 }}>
                {isLoading ?
                    <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', rowGap: 10 }}>
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        <MyAppText style={{ fontSize: 16, color: theme.colors.primaryButton.background }}>{translate('loading')}...</MyAppText>
                    </View>
                    :
                    <>
                        <ScrollView contentContainerStyle={{ rowGap: 12 }} nestedScrollEnabled>
                            <CollapsibleFilter
                                label={translate('share_mosaic')}
                                isOpen={openFilter === 'share'}
                                onToggle={() => toggleFilter('share')}
                            >
                                <View style={styles.treeContainer}>
                                    <View style={styles.countRow}>
                                        <MyAppText style={styles.countText}>
                                            {translate('selected')}: {selectedUsers.length}
                                        </MyAppText>
                                        <TouchableOpacity
                                            onPress={() => {
                                                setSelectedActingBody(0);
                                                setSelectedActingBodyUnit(0);
                                                setSelectedUsers([]);
                                                setShareType(ShareType.ACTING_BODY);
                                            }}
                                            disabled={!hasSelection}
                                        >
                                            <MyAppText style={[styles.clearSelection, !hasSelection && styles.clearSelectionDisabled]}>
                                                {translate('clearSelection')}
                                            </MyAppText>
                                        </TouchableOpacity>
                                    </View>

                                    {mode === 'unit' && ownActingBodyUnit && renderActingBodyUnit(ownActingBodyUnit)}

                                    {mode === 'body' && allActingBodiesUnits.map(renderActingBodyUnit)}

                                    {mode === 'global' && selectableActingBodies.map((actingBody) => {
                                        const isSelected = selectedActingBody === actingBody.value;
                                        return (
                                            <View key={actingBody.value}>
                                                <TouchableOpacity
                                                    style={styles.bodyRow}
                                                    onPress={() => selectActingBody(actingBody.value)}
                                                >
                                                    <View style={styles.bodyLeft}>
                                                        <View style={[styles.radio, isSelected && styles.radioSelected]}>
                                                            {isSelected && <View style={styles.radioInner} />}
                                                        </View>
                                                        <MyAppText style={styles.bodyLabel}>{actingBody.label}</MyAppText>
                                                    </View>
                                                    <FontAwesomeIcon
                                                        icon={(isSelected ? faChevronUp : faChevronDown) as IconDefinition}
                                                        fontSize={12}
                                                        color={theme.colors.primary['500']}
                                                    />
                                                </TouchableOpacity>

                                                {isSelected && (
                                                    <View style={styles.unitsContainer}>
                                                        {actingBodiesUnitsOf(actingBody.value).map(renderActingBodyUnit)}
                                                    </View>
                                                )}
                                            </View>
                                        );
                                    })}
                                </View>
                            </CollapsibleFilter>
                        </ScrollView>

                    </>
                }
            </View>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    treeContainer: {
        gap: 8,
    },
    countRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    countText: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.labelColor,
    },
    clearSelection: {
        fontSize: 14,
        fontWeight: 'bold',
        color: theme.colors.primary['500'],
    },
    clearSelectionDisabled: {
        opacity: 0.4,
    },
    bodyRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    bodyLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
    bodyLabel: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    radio: {
        width: 20,
        height: 20,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: theme.colors.neutral['300'],
        alignItems: 'center',
        justifyContent: 'center',
    },
    radioSelected: {
        borderColor: theme.colors.primary['500'],
    },
    radioInner: {
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: theme.colors.primary['500'],
    },
    unitsContainer: {
        paddingLeft: 34,
        paddingTop: 8,
        gap: 8,
    },
    unitRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingVertical: 8,
    },
    unitLabel: {
        fontSize: 16,
        color: theme.colors.labelColor,
    },
    usersContainer: {
        paddingLeft: 34,
        paddingTop: 12,
    },
    emptyText: {
        fontSize: 14,
        color: theme.colors.neutral['300'],
    },
    unitLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1,
    },
}));
